import * as d3 from 'd3';

/**
 * n8n-Inspired Dark Theme Node Rendering System
 * 
 * Creates beautiful, large nodes with proper visual distinction
 * based on node types (event, operation, condition, etc.)
 * Optimized for dark theme aesthetics
 */

// Dark theme color palette
const DARK_COLORS = {
  background: '#1a1d23', // Main dark background
  cardBg: '#2a2f3a', // Node background
  cardBorder: '#3a404b', // Node borders
  primary: '#4fd1c7', // Teal primary
  secondary: '#7c3aed', // Purple secondary
  accent: '#f59e0b', // Amber accent
  text: '#e5e7eb', // Light text
  textMuted: '#9ca3af', // Muted text
  success: '#10b981', // Green success
  warning: '#f59e0b', // Amber warning
  error: '#ef4444', // Red error
  info: '#3b82f6', // Blue info
};

// n8n-inspired dark theme node configurations with original names
const NODE_CONFIGS = {
  event: {
    width: 200,
    height: 80,
    color: '#4fd1c7', // Teal for events
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#4fd1c7',
    gradientColors: ['#4fd1c7', '#0891b2'],
    icon: '⚡',
    shape: 'rounded'
  },
  operation: {
    width: 200,
    height: 80,
    color: '#7c3aed', // Purple for operations
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#7c3aed',
    gradientColors: ['#7c3aed', '#5b21b6'],
    icon: '⚙',
    shape: 'rounded'
  },
  condition: {
    width: 160,
    height: 100,
    color: '#f59e0b', // Amber for conditions
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#f59e0b',
    gradientColors: ['#f59e0b', '#d97706'],
    icon: '?',
    shape: 'diamond'
  },
  switch: {
    width: 120,
    height: 60,
    color: '#3b82f6', // Blue for switches
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#3b82f6',
    gradientColors: ['#3b82f6', '#1d4ed8'],
    icon: '⊃⊂',
    shape: 'parallelogram'
  },
  each: {
    width: 200,
    height: 80,
    color: '#10b981', // Green for loops
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#10b981',
    gradientColors: ['#10b981', '#047857'],
    icon: '[...]',
    shape: 'hexagon'
  },
  expression: {
    width: 200,
    height: 80,
    color: '#8b5cf6', // Light purple for expressions
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#8b5cf6',
    gradientColors: ['#8b5cf6', '#7c3aed'],
    icon: '{}',
    shape: 'rounded'
  },
  recycle: {
    width: 200,
    height: 80,
    color: '#06b6d4', // Cyan for recycle
    bgColor: DARK_COLORS.cardBg,
    borderColor: '#06b6d4',
    gradientColors: ['#06b6d4', '#0891b2'],
    icon: '♻',
    shape: 'rounded'
  }
};

/**
 * Main render function - n8n dark theme style
 */
function enhancedDarkRender(g, node, isSelected) {
  // Determine node type and get configuration
  const nodeType = getNodeType(node);
  const config = getNodeConfig(nodeType, node);
  
  // Update node dimensions
  node.width = config.width;
  node.height = config.height;
  
  // Create filters and gradients if not exists
  createDarkThemeFilters();
  createNodeGradients();
  
  // Main container
  const container = g.append('g')
    .attr('class', 'n8n-dark-node')
    .style('cursor', 'pointer');
  
  if (config.shape === 'diamond') {
    renderDarkDiamondNode(container, node, config, isSelected);
  } else if (config.shape === 'hexagon') {
    renderDarkHexagonNode(container, node, config, isSelected);
  } else if (config.shape === 'parallelogram') {
    renderDarkParallelogramNode(container, node, config, isSelected);
  } else {
    renderDarkRoundedNode(container, node, config, isSelected);
  }
  
  // Add hover effects
  addHoverEffects(container, node);
  
  return container;
}

/**
 * Determine node type based on node data - keeping original names
 */
function getNodeType(node) {
  // Return default if node is undefined or null
  if (!node) {
    return 'operation';
  }
  
  // Check for recycle event first (special case of event type)
  if (node.data && (node.data.name === 'Recycle' || node.data.action === 'recycle')) {
    return 'recycle';
  }
  if (node.data && node.data.name) {
    const name = node.data.name.toLowerCase();
    if (name.includes('recycle')) return 'recycle';
  }
  
  // Check node.data.type (from your workflow data)
  if (node.data && node.data.type) {
    return node.data.type;
  }
  // Fallback to node.type
  if (node.type) {
    return node.type;
  }
  // Check node.data.name for other specific types
  if (node.data && node.data.name) {
    const name = node.data.name.toLowerCase();
    if (name.includes('condition')) return 'condition';
    if (name.includes('switch')) return 'switch';
    if (name.includes('each')) return 'each';
  }
  // Check node.data.action for expressions
  if (node.data && node.data.action === 'expression') {
    return 'expression';
  }
  
  return 'operation'; // default
}

function isIntegrationOperation(node) {
  if (!node || !node.data) {
    return false;
  }

  return node.data.action === 'integration' || !!node.data.integration_id || !!node.data._integration;
}

function getNodeConfig(nodeType, node) {
  const baseConfig = NODE_CONFIGS[nodeType] || NODE_CONFIGS.operation;

  if (nodeType === 'operation' && isIntegrationOperation(node)) {
    return {
      ...baseConfig,
      color: '#0ea5e9',
      borderColor: '#0ea5e9',
      gradientColors: ['#0ea5e9', '#0369a1']
    };
  }

  return baseConfig;
}

/**
 * Map Bootstrap icon names to semantic emoji for SVG rendering
 */
function bIconToEmoji(bIconName) {
  var map = {
    'mail': '📧',
    'envelope': '📧',
    'envelope-fill': '📧',
    'envelope-open': '📬',
    'envelope-open-fill': '📬',
    'send': '📤',
    'send-fill': '📤',
    'inbox': '📥',
    'inbox-fill': '📥',
    'robot': '🤖',
    'receipt': '🧾',
    'receipt-cutoff': '🧾',
    'shop': '🛒',
    'shop-window': '🛒',
    'life-preserver': '🛟',
    'file-earmark': '📄',
    'file-earmark-text': '📄',
    'file-text': '📄',
    'file-earmark-code': '📋',
    'bell': '🔔',
    'bell-fill': '🔔',
    'globe': '🌐',
    'globe2': '🌐',
    'gear': '⚙️',
    'gear-fill': '⚙️',
    'truck': '🚚',
    'box-seam': '📦',
    'box': '📦',
    'bank': '🏦',
    'bank2': '🏦',
    'credit-card': '💳',
    'credit-card-fill': '💳',
    'person': '👤',
    'person-fill': '👤',
    'people': '👥',
    'chat': '💬',
    'chat-text': '💬',
    'chat-dots': '💬',
    'database': '🗄️',
    'cloud': '☁️',
    'cloud-upload': '☁️',
    'lightning': '⚡',
    'lightning-fill': '⚡',
    'arrow-repeat': '🔁',
    'check-circle': '✅',
    'x-circle': '❌',
    'exclamation-triangle': '⚠️',
  };
  return map[bIconName] || '⚙️';
}

/**
 * Get the actual icon for a node, only using custom icons for operation nodes
 */
function getNodeIcon(node, config) {
  // Get the node type
  var nodeType = getNodeType(node);
  
  // Only use custom icons for operation nodes
  if (nodeType === 'operation') {
    // Resolve icon from most specific to least specific
    var icon = null;
    if (node.data && node.data._integration && node.data._integration.node_icon) {
      icon = node.data._integration.node_icon;
    } else if (node.data && node.data.node_icon) {
      icon = node.data.node_icon;
    } else if (node.icon) {
      icon = node.icon;
    } else if (node.data && node.data.icon) {
      icon = node.data.icon;
    }

    if (icon) {
      if (typeof icon === 'string' && icon.startsWith('b-icon:')) {
        return bIconToEmoji(icon.substring(7));
      }
      // image: and img: icons are handled by getNodeImageUrl; return a placeholder
      if (typeof icon === 'string' && (icon.startsWith('image:') || icon.startsWith('img:'))) {
        return '🔌';
      }
      return icon;
    }
    
    // Fallback based on action
    var action = (node.data && node.data.action) || node.action;
    if (action) {
      switch (action) {
        case 'expression': return '{}';
        case 'email': return '📧';
        case 'http_request': return '🌐';
        case 'platform_notification': return '🔔';
        case 'integration': return '🔌';
        default: return '⚙️';
      }
    }
  }
  
  // For all other node types (event, condition, etc.), use config icon
  return config.icon;
}

function normalizeIconImage(icon) {
  if (!icon || typeof icon !== 'string') {
    return null;
  }
  if (icon.startsWith('image:')) {
    return icon.substring(6);
  }
  if (icon.startsWith('img:')) {
    return icon.substring(4);
  }
  return null;
}

function getNodeImageUrl(node) {
  if (!node) {
    return null;
  }

  var directIcon = normalizeIconImage(node.icon);
  if (directIcon) {
    return directIcon;
  }

  if (!node.data) {
    return null;
  }

  var nodeIcon = normalizeIconImage(node.data.node_icon);
  if (nodeIcon) {
    return nodeIcon;
  }

  var dataIcon = normalizeIconImage(node.data.icon);
  if (dataIcon) {
    return dataIcon;
  }

  if (node.data._integration) {
    var integrationNodeIcon = normalizeIconImage(node.data._integration.node_icon);
    if (integrationNodeIcon) {
      return integrationNodeIcon;
    }
    // Fall back to the integration-level logo when the node icon is not an image
    var integrationIcon = normalizeIconImage(node.data._integration.integration_icon);
    if (integrationIcon) {
      return integrationIcon;
    }
  }

  return null;
}

/**
 * Create dark theme filters and effects
 */
function createDarkThemeFilters() {
  const svg = d3.select('#svg');
  let defs = svg.select('defs');
  if (defs.empty()) {
    defs = svg.append('defs');
  }
  
  // Dark theme shadow
  if (defs.select('#dark-node-shadow').empty()) {
    const filter = defs.append('filter')
      .attr('id', 'dark-node-shadow')
      .attr('width', '150%')
      .attr('height', '150%');
    
    filter.append('feDropShadow')
      .attr('dx', 0)
      .attr('dy', 4)
      .attr('stdDeviation', 6)
      .attr('flood-color', 'rgba(0, 0, 0, 0.4)');
  }
  
  // Glow effect for selected nodes
  if (defs.select('#dark-node-glow').empty()) {
    const glowFilter = defs.append('filter')
      .attr('id', 'dark-node-glow')
      .attr('width', '150%')
      .attr('height', '150%');
    
    glowFilter.append('feGaussianBlur')
      .attr('stdDeviation', '3')
      .attr('result', 'coloredBlur');
    
    const feMerge = glowFilter.append('feMerge');
    feMerge.append('feMergeNode').attr('in', 'coloredBlur');
    feMerge.append('feMergeNode').attr('in', 'SourceGraphic');
  }
}

/**
 * Create gradients for node backgrounds
 */
function createNodeGradients() {
  const svg = d3.select('#svg');
  let defs = svg.select('defs');
  if (defs.empty()) {
    defs = svg.append('defs');
  }
  
  // Create gradients for each node type
  Object.keys(NODE_CONFIGS).forEach(nodeType => {
    const config = NODE_CONFIGS[nodeType];
    const gradientId = `${nodeType}-gradient`;
    
    if (defs.select(`#${gradientId}`).empty()) {
      const gradient = defs.append('linearGradient')
        .attr('id', gradientId)
        .attr('x1', '0%')
        .attr('y1', '0%')
        .attr('x2', '100%')
        .attr('y2', '100%');
      
      gradient.append('stop')
        .attr('offset', '0%')
        .attr('stop-color', config.gradientColors[0])
        .attr('stop-opacity', 0.1);
      
      gradient.append('stop')
        .attr('offset', '100%')
        .attr('stop-color', config.gradientColors[1])
        .attr('stop-opacity', 0.05);
    }
  });
}

/**
 * Render dark theme rounded rectangle node (most node types)
 */
function renderDarkRoundedNode(container, node, config, isSelected) {
  const nodeType = getNodeType(node);
  const borderWidth = isSelected ? 3 : 2;
  
  // Main background rectangle with gradient
  container.append('rect')
    .attr('x', node.x)
    .attr('y', node.y)
    .attr('width', config.width)
    .attr('height', config.height)
    .attr('rx', 12)
    .attr('ry', 12)
    .attr('fill', `url(#${nodeType}-gradient)`)
    .attr('stroke', isSelected ? DARK_COLORS.primary : config.borderColor)
    .attr('stroke-width', borderWidth)
    .style('filter', isSelected ? 'url(#dark-node-glow)' : 'url(#dark-node-shadow)');
  
  // Dark background overlay
  container.append('rect')
    .attr('x', node.x)
    .attr('y', node.y)
    .attr('width', config.width)
    .attr('height', config.height)
    .attr('rx', 12)
    .attr('ry', 12)
    .attr('fill', config.bgColor)
    .attr('opacity', 0.95);
  
  // Icon background circle with glow
  const iconX = node.x + 24;
  const iconY = node.y + config.height / 2;
  
  container.append('circle')
    .attr('cx', iconX)
    .attr('cy', iconY)
    .attr('r', 20)
    .attr('fill', config.color)
    .attr('opacity', 0.15);
  
  container.append('circle')
    .attr('cx', iconX)
    .attr('cy', iconY)
    .attr('r', 16)
    .attr('fill', config.color)
    .attr('opacity', 0.2);
  
  // Icon - Fixed centering with proper alignment
  const nodeIcon = getNodeIcon(node, config);
  const iconImageUrl = getNodeImageUrl(node);
  const iconGroup = container.append('g')
    .attr('transform', `translate(${iconX}, ${iconY})`);

  if (iconImageUrl) {
    iconGroup.append('image')
      .attr('x', -10)
      .attr('y', -10)
      .attr('width', 20)
      .attr('height', 20)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('href', iconImageUrl);
  } else {
    iconGroup.append('text')
      .attr('x', 0)
      .attr('y', 0)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('dy', '0.035em')
      .attr('font-size', '16px')
      .attr('fill', config.color)
      .attr('font-weight', 'bold')
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .text(nodeIcon);
  }
  
  // Node title with dark theme colors
  const titleX = node.x + 56;
  const titleY = node.y + config.height / 2 - 6;
  
  const title = getNodeTitle(node);
  container.append('text')
    .attr('x', titleX)
    .attr('y', titleY)
    .attr('font-size', '15px')
    .attr('font-weight', '600')
    .attr('fill', DARK_COLORS.text)
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .text(truncateText(title, config.width - 80));
  
  // Node subtitle/description with muted color
  const subtitle = getNodeSubtitle(node);
  if (subtitle) {
    container.append('text')
      .attr('x', titleX)
      .attr('y', titleY + 18)
      .attr('font-size', '12px')
      .attr('font-weight', '400')
      .attr('fill', DARK_COLORS.textMuted)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .text(truncateText(subtitle, config.width - 80));
  }

  // Node technical name (e.g. "Completion_1") shown dimly below subtitle
  var nodeTechName = node.name || (node.data && node.data.name);
  if (nodeTechName) {
    var nameY = subtitle ? titleY + 32 : titleY + 18;
    container.append('text')
      .attr('x', titleX)
      .attr('y', nameY)
      .attr('font-size', '10px')
      .attr('font-weight', '400')
      .attr('fill', DARK_COLORS.textMuted)
      .attr('opacity', 0.5)
      .attr('font-family', 'monospace, system-ui, sans-serif')
      .text(truncateText(nodeTechName, config.width - 80));
  }
  
  // Play button (visible on hover)
  addPlayButton(container, node, config);

  // Status indicator with dark theme
  if (node.data && node.data.paused) {
    container.append('circle')
      .attr('cx', node.x + config.width - 18)
      .attr('cy', node.y + 18)
      .attr('r', 8)
      .attr('fill', DARK_COLORS.warning)
      .attr('stroke', DARK_COLORS.cardBg)
      .attr('stroke-width', 2);
    
    container.append('text')
      .attr('x', node.x + config.width - 18)
      .attr('y', node.y + 18)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'central')
      .attr('font-size', '10px')
      .attr('fill', DARK_COLORS.background)
      .attr('font-weight', 'bold')
      .text('⏸');
  }
  
  // Connection points indicators  
  addConnectionPoints(container, node, config);
}

/**
 * Render dark theme diamond node (for conditions)
 */
function renderDarkDiamondNode(container, node, config, isSelected) {
  const nodeType = getNodeType(node);
  const borderWidth = isSelected ? 3 : 2;
  const cx = node.x + config.width / 2;
  const cy = node.y + config.height / 2;
  const w = config.width / 2;
  const h = config.height / 2;
  
  // Diamond path
  const path = `M ${cx} ${cy - h} L ${cx + w} ${cy} L ${cx} ${cy + h} L ${cx - w} ${cy} Z`;
  
  // Background diamond with gradient
  container.append('path')
    .attr('d', path)
    .attr('fill', `url(#${nodeType}-gradient)`)
    .attr('stroke', isSelected ? DARK_COLORS.primary : config.borderColor)
    .attr('stroke-width', borderWidth)
    .style('filter', isSelected ? 'url(#dark-node-glow)' : 'url(#dark-node-shadow)');
  
  // Dark overlay
  container.append('path')
    .attr('d', path)
    .attr('fill', config.bgColor)
    .attr('opacity', 0.95);
  
  // Get title to determine positioning
  const title = getNodeTitle(node);
  const hasTitle = title && title.length > 0;
  
  // Icon positioning - center if no title, otherwise offset upward
  const iconY = hasTitle ? cy - 12 : cy;
  
  // Icon background circle
  container.append('circle')
    .attr('cx', cx)
    .attr('cy', iconY)
    .attr('r', 16)
    .attr('fill', config.color)
    .attr('opacity', 0.15);
  
  // Icon - Centered or offset based on title presence
  const nodeIcon = getNodeIcon(node, config);
  container.append('text')
    .attr('x', cx)
    .attr('y', iconY)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', '18px')
    .attr('fill', config.color)
    .attr('font-weight', 'bold')
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .text(nodeIcon);
  
  // Title with dark theme colors (only if title exists)
  if (hasTitle) {
    container.append('text')
      .attr('x', cx)
      .attr('y', cy + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', '600')
      .attr('fill', DARK_COLORS.text)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .text(truncateText(title, config.width - 40));
  }
  
  // Play button for diamond
  addPlayButton(container, node, config);

  // Connection points for diamond
  addConnectionPoints(container, { ...node, x: cx - w, y: cy - h, width: w * 2, height: h * 2 }, config);
}

/**
 * Get node title based on node data, with specific handling per node type
 */
function getNodeTitle(node) {
  const nodeType = getNodeType(node);
  
  // For all event nodes, prioritize label, then fall back to name
  if (node.data && node.data.type === 'event') {
    // Check for label first (translated text like "customEvent")
    if (node.data.label) return node.data.label;
    if (node.label) return node.label;
    
    // Fall back to the custom name (like "test_node" or "CustomEvent_1")
    if (node.data.name) return node.data.name;
    if (node.name) return node.name;
  }
  
  // For operation nodes, use the node name
  if (nodeType === 'operation') {
    var nodeAction = (node.data && node.data.action) || node.action;
    if (nodeAction === 'integration') {
      if (node.data && node.data.node_label) return resolveIntegrationLabel(node.data.node_label, node.data.node_name || 'Integration Node');
      if (node.data && node.data._integration && node.data._integration.node_label) {
        return resolveIntegrationLabel(node.data._integration.node_label, node.data.node_name || node.data._integration.node_name || 'Integration Node');
      }
      // Prefer the already-translated top-level label set at node creation time
      if (node.label) return node.label;
    }
    // Check all possible locations for the name
    if (node.name) return node.name;
    if (node.data && node.data.name) return node.data.name;
    if (node.data && node.data.id && node.data.id !== node.name) return node.data.id;
  }
  
  // For condition, switch, and each nodes, show no title (only icon)
  if (nodeType === 'condition' || nodeType === 'switch' || nodeType === 'each') {
    return '';
  }
  
  // For other nodes, check for label first
  if (node.label) return node.label;
  if (node.data && node.data.label) return node.data.label;
  
  // Fallback to name
  if (node.name) return node.name;
  if (node.data && node.data.name) return node.data.name;
  
  // Final fallback
  if (node.data && node.data.type) {
    return capitalizeFirst(node.data.type);
  }
  return 'Untitled Node';
}

/**
 * Get node subtitle/description - keeping original terminology
 */
function getNodeSubtitle(node) {
  const nodeType = getNodeType(node);
  
  switch (nodeType) {
    case 'event':
      // Show schema if available for event nodes
      // Check node.schema at top level first
      if (node.schema) {
        return `${node.schema}`;
      }
      // Check node.data.schema
      if (node.data && node.data.schema) {
        return `${node.data.schema}`;
      }
      // Try to extract from the full event name format (schema::eventName)
      if (node.name && node.name.includes('::')) {
        const parts = node.name.split('::');
        if (parts.length >= 2) {
          // Return all but the last part (the event name)
          return parts.slice(0, -1).join('::');
        }
      }
      if (node.data && node.data.name && node.data.name.includes('::')) {
        const parts = node.data.name.split('::');
        if (parts.length >= 2) {
          return parts.slice(0, -1).join('::');
        }
      }
      return 'Event';
    case 'operation':
      var opAction = (node.data && node.data.action) || node.action;
      if (opAction === 'integration') {
        if (node.data && node.data.integration_label) {
          return resolveIntegrationLabel(node.data.integration_label, node.data.integration_id || 'Integration');
        }
        if (node.data && node.data._integration && node.data._integration.integration_label) {
          return resolveIntegrationLabel(node.data._integration.integration_label, node.data._integration.integration_id || 'Integration');
        }
        return 'Integration';
      }
      // For built-in operation nodes, show a friendly action label
      if (opAction) {
        switch (opAction) {
          case 'email': return 'Send email';
          case 'http_request': return 'HTTP request';
          case 'platform_notification': return 'Push notification';
          case 'expression': return 'Expression';
          default: return opAction.replace(/_/g, ' ');
        }
      }
      return 'Operation';
    case 'condition':
      return null; // No subtitle for condition nodes
    case 'switch':
      return 'Multiple paths';
    case 'each':
      return 'Repeats items';
    case 'expression':
      return 'Calculates value';
    case 'recycle':
      // Show timer information if available
      if (node.data && node.data.timer) {
        return `Every ${node.data.timer}`;
      }
      return 'Timer trigger';
    default:
      return '';
  }
}

function resolveIntegrationLabel(label, fallback) {
  if (typeof label === 'string' && label.length > 0) {
    return label;
  }

  if (label && typeof label === 'object' && label.locales) {
    if (label.locales.en_US) {
      return label.locales.en_US;
    }
    if (label.locales.sr) {
      return label.locales.sr;
    }
  }

  if (label && typeof label === 'object') {
    if (label.en_US) {
      return label.en_US;
    }
    if (label.sr) {
      return label.sr;
    }

    var keys = Object.keys(label);
    if (keys.length > 0 && typeof label[keys[0]] === 'string') {
      return label[keys[0]];
    }
  }

  return fallback || 'Integration';
}

/**
 * Truncate text to fit width
 */
function truncateText(text, maxWidth) {
  const avgCharWidth = 7; // approximate character width
  const maxChars = Math.floor(maxWidth / avgCharWidth);
  
  if (text.length <= maxChars) return text;
  return text.substring(0, maxChars - 3) + '...';
}

/**
 * Capitalize first letter
 */
function capitalizeFirst(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

/**
 * Add play button to node (visible on hover)
 */
function addPlayButton(container, node, config) {
  if (node.type !== 'event') return;

  var btnX = node.x + config.width - 28;
  var btnY = node.y + config.height - 24;

  // For diamond/hexagon/parallelogram shapes, position at center-bottom area
  if (config.shape === 'diamond' || config.shape === 'hexagon' || config.shape === 'parallelogram') {
    btnX = node.x + config.width / 2 + 20;
    btnY = node.y + config.height / 2 + 14;
  }

  var playGroup = container.append('g')
    .attr('class', 'node-play-btn')
    .attr('data-node-id', node.id)
    .attr('transform', 'translate(' + btnX + ',' + btnY + ')')
    .style('cursor', 'pointer')
    .style('opacity', 0);

  playGroup.append('circle')
    .attr('r', 12)
    .attr('fill', DARK_COLORS.success)
    .attr('opacity', 0.9);

  // Play triangle
  playGroup.append('path')
    .attr('d', 'M -3 -5 L -3 5 L 5 0 Z')
    .attr('fill', '#ffffff');
}

/**
 * Add connection points to nodes
 */
function addConnectionPoints(container, node, config) {
  const points = [
    { x: node.x, y: node.y + config.height / 2, type: 'input' }, // Left
    { x: node.x + config.width, y: node.y + config.height / 2, type: 'output' }, // Right
    { x: node.x + config.width / 2, y: node.y, type: 'input' }, // Top
    { x: node.x + config.width / 2, y: node.y + config.height, type: 'output' } // Bottom
  ];
  
  points.forEach(point => {
    container.append('circle')
      .attr('cx', point.x)
      .attr('cy', point.y)
      .attr('r', 4)
      .attr('fill', point.type === 'input' ? DARK_COLORS.success : DARK_COLORS.primary)
      .attr('stroke', DARK_COLORS.cardBg)
      .attr('stroke-width', 2)
      .attr('opacity', 0)
      .attr('class', `connection-point ${point.type}`)
      .style('transition', 'opacity 0.2s');
  });
}

/**
 * Add hover effects to nodes
 */
function addHoverEffects(container, node) {
  const nodeType = getNodeType(node);
  const shouldShowTooltip = nodeType === 'operation' || nodeType === 'event';
  
  container
    .on('mouseenter', function() {
      // Show connection points
      d3.select(this).selectAll('.connection-point')
        .transition()
        .duration(200)
        .attr('opacity', 0.8);

      // Show play button
      d3.select(this).selectAll('.node-play-btn')
        .transition()
        .duration(200)
        .style('opacity', 1);

      // Slightly brighten the node
      d3.select(this).select('rect, path')
        .transition()
        .duration(200)
        .attr('opacity', 1);

      // Show tooltip for operation and event nodes
      if (shouldShowTooltip) {
        showNodeTooltip(node, d3.event);
      }
    })
    .on('mouseleave', function() {
      // Hide connection points
      d3.select(this).selectAll('.connection-point')
        .transition()
        .duration(200)
        .attr('opacity', 0);

      // Hide play button
      d3.select(this).selectAll('.node-play-btn')
        .transition()
        .duration(200)
        .style('opacity', 0);

      // Return to normal opacity
      d3.select(this).select('rect, path')
        .transition()
        .duration(200)
        .attr('opacity', 0.95);

      // Hide tooltip
      if (shouldShowTooltip) {
        hideNodeTooltip();
      }
    })
    .on('mousemove', function() {
      // Update tooltip position if visible
      if (shouldShowTooltip) {
        updateTooltipPosition(d3.event);
      }
    });
}

/**
 * Show tooltip with node label
 */
function showNodeTooltip(node, event) {
  // Get the full node label (prioritize label over name)
  let tooltipText = '';
  if (node.label) {
    tooltipText = node.label;
  } else if (node.data && node.data.label) {
    tooltipText = node.data.label;
  } else if (node.name) {
    tooltipText = node.name;
  } else if (node.data && node.data.name) {
    tooltipText = node.data.name;
  }
  
  // Don't show tooltip if no meaningful name
  if (!tooltipText || tooltipText.length <= 15) {
    return;
  }
  
  // Remove any existing tooltip
  d3.select('body').select('.node-tooltip').remove();
  
  // Create tooltip
  const tooltip = d3.select('body')
    .append('div')
    .attr('class', 'node-tooltip')
    .style('position', 'absolute')
    .style('background', DARK_COLORS.cardBg)
    .style('border', `1px solid ${DARK_COLORS.primary}`)
    .style('border-radius', '6px')
    .style('padding', '8px 12px')
    .style('font-size', '13px')
    .style('font-weight', '500')
    .style('color', DARK_COLORS.text)
    .style('max-width', '300px')
    .style('word-wrap', 'break-word')
    .style('box-shadow', '0 4px 12px rgba(0, 0, 0, 0.3)')
    .style('z-index', '10000')
    .style('opacity', 0)
    .style('pointer-events', 'none')
    .style('font-family', 'system-ui, -apple-system, sans-serif')
    .text(tooltipText);
  
  // Position tooltip
  updateTooltipPosition(event);
  
  // Fade in
  tooltip.transition()
    .duration(200)
    .style('opacity', 1);
}

/**
 * Update tooltip position
 */
function updateTooltipPosition(event) {
  const tooltip = d3.select('.node-tooltip');
  if (tooltip.empty()) return;
  
  tooltip
    .style('left', (event.pageX + 10) + 'px')
    .style('top', (event.pageY - 30) + 'px');
}

/**
 * Hide tooltip
 */
function hideNodeTooltip() {
  d3.select('.node-tooltip')
    .transition()
    .duration(200)
    .style('opacity', 0)
    .on('end', function() {
      d3.select(this).remove();
    });
}

/**
 * Render a dark themed hexagon node (for each/loop nodes)
 */
function renderDarkHexagonNode(container, node, config, isSelected) {
  const nodeType = getNodeType(node);
  const borderWidth = isSelected ? 3 : 2;
  const cx = node.x + config.width / 2;
  const cy = node.y + config.height / 2;
  const w = config.width / 2;
  const h = config.height / 2;
  
  // Hexagon path (6 sides)
  const radius = Math.min(w, h) * 0.9;
  const points = [];
  for (let i = 0; i < 6; i++) {
    const angle = (i * Math.PI) / 3;
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);
    points.push(`${x},${y}`);
  }
  const path = `M ${points.join(' L ')} Z`;
  
  // Background hexagon with gradient
  container.append('path')
    .attr('d', path)
    .attr('fill', `url(#${nodeType}-gradient)`)
    .attr('stroke', isSelected ? DARK_COLORS.primary : config.borderColor)
    .attr('stroke-width', borderWidth)
    .style('filter', isSelected ? 'url(#dark-node-glow)' : 'url(#dark-node-shadow)');
  
  // Dark overlay
  container.append('path')
    .attr('d', path)
    .attr('fill', config.bgColor)
    .attr('opacity', 0.95);
  
  // Get title to determine positioning
  const title = getNodeTitle(node);
  const hasTitle = title && title.length > 0;
  
  // Icon positioning - center if no title, otherwise offset upward
  const iconY = hasTitle ? cy - 12 : cy;
  
  // Icon background circle
  container.append('circle')
    .attr('cx', cx)
    .attr('cy', iconY)
    .attr('r', 16)
    .attr('fill', config.color)
    .attr('opacity', 0.15);
  
  // Icon - Centered or offset based on title presence
  const nodeIcon = getNodeIcon(node, config);
  container.append('text')
    .attr('x', cx)
    .attr('y', iconY)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', '18px')
    .attr('fill', config.color)
    .attr('font-weight', 'bold')
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .text(nodeIcon);
  
  // Title with dark theme colors (only if title exists)
  if (hasTitle) {
    container.append('text')
      .attr('x', cx)
      .attr('y', cy + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', '600')
      .attr('fill', DARK_COLORS.text)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .text(truncateText(title, config.width - 40));
  }

  // Play button for hexagon
  addPlayButton(container, node, config);
}

/**
 * Render a dark themed parallelogram node (for switch nodes)
 */
function renderDarkParallelogramNode(container, node, config, isSelected) {
  const nodeType = getNodeType(node);
  const borderWidth = isSelected ? 3 : 2;
  const cx = node.x + config.width / 2;
  const cy = node.y + config.height / 2;
  const w = config.width / 2;
  const h = config.height / 2;
  const skew = w * 0.2; // Skew factor for parallelogram
  
  // Parallelogram path (skewed rectangle)
  const path = `M ${cx - w + skew} ${cy - h} L ${cx + w + skew} ${cy - h} L ${cx + w - skew} ${cy + h} L ${cx - w - skew} ${cy + h} Z`;
  
  // Background parallelogram with gradient
  container.append('path')
    .attr('d', path)
    .attr('fill', `url(#${nodeType}-gradient)`)
    .attr('stroke', isSelected ? DARK_COLORS.primary : config.borderColor)
    .attr('stroke-width', borderWidth)
    .style('filter', isSelected ? 'url(#dark-node-glow)' : 'url(#dark-node-shadow)');
  
  // Dark overlay
  container.append('path')
    .attr('d', path)
    .attr('fill', config.bgColor)
    .attr('opacity', 0.95);
  
  // Get title to determine positioning
  const title = getNodeTitle(node);
  const hasTitle = title && title.length > 0;
  
  // Icon positioning - center if no title, otherwise offset upward
  const iconY = hasTitle ? cy - 12 : cy;
  
  // Icon background circle
  container.append('circle')
    .attr('cx', cx)
    .attr('cy', iconY)
    .attr('r', 16)
    .attr('fill', config.color)
    .attr('opacity', 0.15);
  
  // Icon - Centered or offset based on title presence
  const nodeIcon = getNodeIcon(node, config);
  container.append('text')
    .attr('x', cx)
    .attr('y', iconY)
    .attr('text-anchor', 'middle')
    .attr('dominant-baseline', 'central')
    .attr('font-size', '18px')
    .attr('fill', config.color)
    .attr('font-weight', 'bold')
    .attr('font-family', 'system-ui, -apple-system, sans-serif')
    .text(nodeIcon);
  
  // Title with dark theme colors (only if title exists)
  if (hasTitle) {
    container.append('text')
      .attr('x', cx)
      .attr('y', cy + 12)
      .attr('text-anchor', 'middle')
      .attr('font-size', '13px')
      .attr('font-weight', '600')
      .attr('fill', DARK_COLORS.text)
      .attr('font-family', 'system-ui, -apple-system, sans-serif')
      .text(truncateText(title, config.width - 40));
  }

  // Play button for parallelogram
  addPlayButton(container, node, config);
}

/**
 * Get custom connector positions for different node shapes
 */
function getCustomConnectorPosition(node) {
  // Safety check: ensure node exists and has required properties
  if (!node || typeof node.x === 'undefined' || typeof node.y === 'undefined') {
    console.warn('getCustomConnectorPosition called with invalid node:', node);
    return {
      left: { x: 0, y: 0 },
      right: { x: 0, y: 0 },
      top: { x: 0, y: 0 },
      bottom: { x: 0, y: 0 }
    };
  }
  
  const nodeType = getNodeType(node);
  const config = NODE_CONFIGS[nodeType] || NODE_CONFIGS.operation;
  
  // For hexagon shapes (each nodes)
  if (config.shape === 'hexagon') {
    const cx = node.x + config.width / 2;
    const cy = node.y + config.height / 2;
    const radius = Math.min(config.width / 2, config.height / 2) * 0.9;
    
    return {
      left: { x: cx + radius * Math.cos(Math.PI), y: cy + radius * Math.sin(Math.PI) }, // Left
      right: { x: cx + radius * Math.cos(0), y: cy + radius * Math.sin(0) }, // Right
      top: { x: cx + radius * Math.cos(-Math.PI/2), y: cy + radius * Math.sin(-Math.PI/2) }, // Top
      bottom: { x: cx + radius * Math.cos(Math.PI/2), y: cy + radius * Math.sin(Math.PI/2) } // Bottom
    };
  }
  
  // For parallelogram shapes (switch nodes)
  if (config.shape === 'parallelogram') {
    const cx = node.x + config.width / 2;
    const cy = node.y + config.height / 2;
    const w = config.width / 2;
    const h = config.height / 2;
    const skew = w * 0.2;
    
    return {
      left: { x: cx - w, y: cy }, // Left center
      right: { x: cx + w, y: cy }, // Right center
      top: { x: cx + skew, y: cy - h }, // Top center
      bottom: { x: cx - skew, y: cy + h } // Bottom center
    };
  }
  
  // For diamond shapes (condition nodes)
  if (config.shape === 'diamond') {
    const cx = node.x + config.width / 2;
    const cy = node.y + config.height / 2;
    const w = config.width / 2;
    const h = config.height / 2;
    
    return {
      left: { x: cx - w, y: cy }, // Left point
      right: { x: cx + w, y: cy }, // Right point
      top: { x: cx, y: cy - h }, // Top point
      bottom: { x: cx, y: cy + h } // Bottom point
    };
  }
  
  // Default rectangular connection points for other shapes
  const halfWidth = config.width / 2;
  const halfHeight = config.height / 2;
  return {
    left: { x: node.x, y: node.y + halfHeight },
    right: { x: node.x + config.width, y: node.y + halfHeight },
    top: { x: node.x + halfWidth, y: node.y },
    bottom: { x: node.x + halfWidth, y: node.y + config.height }
  };
}

export { getCustomConnectorPosition };
export default enhancedDarkRender;
